// Supabase client setup
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase URL or Anon Key is missing. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your environment variables.');
}

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
    db: {
      schema: 'public',
    },
    global: {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    },
  }
);

// Database types (matching our schema)
export interface DatabaseUser {
  id: string;
  username: string;
  email: string;
  password_hash?: string; // Only used server-side, not exposed to client
  reset_token?: string | null;
  reset_token_expires_at?: string | null;
  avatar_url?: string | null;
  favorite_genre?: string | null;
  total_submissions?: number;
  is_admin?: boolean;
  created_at: string;
  updated_at: string;
}

export interface DatabaseLeague {
  id: string;
  name: string;
  description?: string | null;
  invite_code: string;
  created_by: string;
  max_players: number;
  points_per_round: number;
  scoring: Record<string, any>;
  require_spotify: boolean;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export interface DatabaseLeagueMember {
  id: string;
  league_id: string;
  user_id: string;
  joined_at: string;
  is_admin?: boolean;
}

export interface DatabaseSeason {
  id: string;
  league_id: string;
  name: string;
  description?: string | null;
  invite_code: string;
  created_by: string | null;
  max_players: number;
  points_per_round: number;
  scoring: Record<string, any>;
  require_spotify: boolean;
  status: 'upcoming' | 'active' | 'completed';
  sort_index?: number | null;
  created_at: string;
  updated_at: string;
}

export interface DatabaseRound {
  id: string;
  league_id: string;
  round_number: number;
  theme: string;
  description?: string | null;
  season?: string | null;
  season_id?: string | null;
  submission_deadline: string;
  voting_deadline: string;
  status: 'submitting' | 'voting' | 'completed';
  created_at: string;
  updated_at: string;
}

export interface DatabaseSubmission {
  id: string;
  round_id: string;
  user_id: string;
  song_title: string;
  artist_name: string;
  spotify_track_id?: string | null;
  spotify_url?: string | null;
  youtube_video_id?: string | null;
  youtube_url?: string | null;
  submitter_comment?: string | null;
  fun_facts?: string[] | null;
  total_points?: number;
  submitted_at: string;
  comments_imported?: boolean | null;
  imported_at?: string | null;
  import_source?: string | null;
}

export interface DatabaseVote {
  id: string;
  round_id: string;
  user_id: string;
  submission_id: string;
  points: number;
  comment?: string;
  created_at: string;
}

export interface DatabaseActivity {
  id: string;
  type: 'vote' | 'submission' | 'result' | 'join' | 'league_created' | 'round_started';
  message: string;
  user_id?: string | null;
  league_id?: string | null;
  round_id?: string | null;
  created_at: string;
}

export interface DatabasePlaylistComment {
  id: string;
  playlist_id: string;
  playlist_type: 'round' | 'league' | 'season' | 'curated' | 'personal';
  user_id: string;
  comment: string;
  created_at: string;
  updated_at: string;
}

export interface DatabasePlaylistLink {
  id: string;
  playlist_type: 'season' | 'round' | 'league' | 'curated' | 'personal';
  playlist_id: string;
  platform: 'spotify' | 'apple_music' | 'amazon_music' | 'youtube' | 'tidal' | 'deezer' | 'soundiiz';
  url: string;
  playlist_name?: string | null;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DatabaseCuratedPlaylist {
  id: string;
  name: string;
  description?: string | null;
  playlist_type: 'curated' | 'system' | 'season_top3';
  filters: Record<string, any>;
  created_by?: string | null;
  is_system_generated: boolean;
  season_id?: string | null;
  sort_index?: number | null;
  smartlink_url?: string | null; // SmartLinks embed URL for this playlist
  created_at: string;
  updated_at: string;
}

